import { useNavigation } from '@react-navigation/core';
import { Image } from 'expo-image';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Animated, { SlideInRight, SlideOutLeft } from 'react-native-reanimated';

import { CorrectIncorrectButton } from '@oui/app-core/src/components/CorrectIncorrectButton/CorrectIncorrectButton';
import { HeaderButtons } from '@oui/app-core/src/components/HeaderButtons';
import { Lead, Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useTheme } from '@oui/app-core/src/styles';

import Illustration from './Illustration.png';
import Pairs from './pairs.json';

function baseRandom(lower: number, upper: number) {
  return lower + Math.floor(Math.random() * (upper - lower + 1));
}

function Card({ text }: { text: string }) {
  const { theme } = useTheme();
  return (
    <View
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 10,
        backgroundColor: theme.color.gray800,
        borderColor: theme.color.gray700,
        height: 130,
      }}
    >
      <Lead text={text} />
    </View>
  );
}

function WordPair({
  onAnswer,
  pair,
  correctFirst,
}: {
  pair: (typeof Pairs)[number];
  correctFirst: boolean;
  onAnswer: (correct: boolean) => unknown;
}) {
  const [answered, setAnswered] = useState(false);

  function submitAnswer(correct: boolean) {
    setAnswered(true);
    return onAnswer(correct);
  }

  const correctButton = (
    <CorrectIncorrectButton text={pair.correct} correct onPress={() => submitAnswer(true)} />
  );
  const incorrectButton = (
    <CorrectIncorrectButton
      text={pair.incorrect}
      correct={false}
      onPress={() => submitAnswer(false)}
    />
  );

  return (
    <View
      style={{
        gap: 25,
        pointerEvents: answered ? 'none' : undefined,
      }}
    >
      <View style={{ marginBottom: 25 }}>
        <Card text={pair.prompt} />
      </View>
      {correctFirst ? (
        <>
          {correctButton}
          {incorrectButton}
        </>
      ) : (
        <>
          {incorrectButton}
          {correctButton}
        </>
      )}
    </View>
  );
}

function useStopwatch() {
  const timeout = useRef<NodeJS.Timeout>(undefined);
  const startMs = useRef<number>(0);
  const [durationMs, setDurationMs] = useState(0);

  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);

  const start = useCallback(() => {
    function advance() {
      startMs.current = Date.now();
      timeout.current = setTimeout(() => {
        setDurationMs((dur) => {
          const newDur = dur + (Date.now() - startMs.current);
          advance();
          return newDur;
        });
      }, 250);
    }
    advance();
  }, []);

  const stop = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
      setDurationMs((dur) => dur + (Date.now() - startMs.current));
    }
  }, []);

  return { start, stop, durationMs };
}

/**
 * Generates word pairs to quiz the user on.
 */
export const WordPairingRound = ({
  numPairs,
  parentPadding,
  onAnswer,
}: {
  numPairs: number;
  parentPadding?: number;
  /**
   * Called for each answer submitted by the user
   */
  onAnswer: (data: { index: number; correct: boolean; timeMs: number }) => unknown;
}) => {
  const pairsRef = useRef([...Pairs]);
  function generateIndexState(index: number = 0) {
    const sampleIndex = baseRandom(0, pairsRef.current.length - 1);
    const [pair] = pairsRef.current.splice(sampleIndex, 1);
    return { index, pair, correctFirst: Math.random() >= 0.5 };
  }

  const { theme } = useTheme();
  const { setOptions } = useNavigation();
  const [roundState, setRoundState] = useState(generateIndexState);
  const { current: startMs } = useRef(Date.now());

  const { start, durationMs } = useStopwatch();

  useEffect(() => {
    start();
  }, [start]);

  const seconds = Math.floor(durationMs / 1000);
  const timer = `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

  useLayoutEffect(() => {
    setOptions({
      headerRight: () => (
        <HeaderButtons>
          <Text text={timer} size={15} />
        </HeaderButtons>
      ),
    });
  }, [timer, setOptions]);

  return (
    <View testID="WordPairingRound" style={{ flex: 1 }}>
      <View
        style={{
          margin: -(parentPadding ?? 0),
          marginBottom: parentPadding ?? 20,
          height: 5,
          backgroundColor: theme.color.accent300,
        }}
      >
        <View
          style={{
            width: `${((roundState.index + 1) / numPairs) * 100}%`,
            height: 5,
            backgroundColor: theme.color.accent100,
          }}
        />
      </View>
      <Image
        source={Illustration}
        style={{ width: 375, height: 165, position: 'absolute', bottom: 0, right: 0 }}
      />
      <Text textAlign="center" text="Choose a sentence for each phrase." />
      <Animated.View
        key={roundState.index.toString()}
        style={{
          flex: 1,
          justifyContent: 'center',
        }}
        entering={SlideInRight.duration(300)}
        exiting={SlideOutLeft.duration(300)}
      >
        <WordPair
          key={roundState.index}
          pair={roundState.pair}
          correctFirst={roundState.correctFirst}
          onAnswer={(correct) => {
            onAnswer({ index: roundState.index, correct, timeMs: Date.now() - startMs });
            if (roundState.index < numPairs - 1) {
              setTimeout(() => setRoundState((i) => generateIndexState(i.index + 1)), 1000);
            }
          }}
        />
      </Animated.View>
    </View>
  );
};
